<template>
    <div
        ref="elMap"
        class="address-map d-flex flex-column align-center justify-center"
        :style="{ width, height }"
    >
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>
</template>

<script lang="ts" setup>
    /// <reference types="@types/google.maps" />
    import type {
        LocationsMapSearch,
        LocationAddress,
        Location
    } from '~/types';
    import type {
        GenericVuetifyListItem
    } from 'x1-base/types';
    import { isError as lo_isError } from 'es-toolkit';
    import { isNull as lo_isNull } from 'es-toolkit';
    import { useSportGroups } from '~/composables';
    import type { MarkerClusterer } from '@googlemaps/markerclusterer';
    import type { WatchStopHandle } from 'vue';

    const props = withDefaults(defineProps<{
        locations?: Nilish<Location[] | Error>;
        search?: LocationsMapSearch;
        width?: string;
        height?: string;
    }>(), {
        locations: () => [],
        search: () => (({}) as LocationsMapSearch),
        width: undefined,
        height: undefined
    });

    const emit = defineEmits<{
        'on-bound-locations': [value: Nilish<string[]>];
        'location-search-error': [value: void];
        'update:markets-filter-list': [value: Map<string, GenericVuetifyListItem>];
    }>();

    const $config = useRuntimeConfig();
    const { sportGroupLabel, sortSportGroups } = useSportGroups();

    const markerBoundsPadding = 40;

    const isMapsLibLoaded = ref(false);
    const isMarkClusterLibLoaded = ref(false);

    const elMap = shallowRef<HTMLElement>();
    const map = ref<google.maps.Map>();
    const mapReady = ref(false);
    const mapCenterCoords = [37.0902, -95.7129];
    const geocoder = ref<google.maps.Geocoder>();

    const apiKey = $config.public.environment.thirdPartyApi.google.maps.key;

    let infoWindow: google.maps.InfoWindow;
    let bounds: google.maps.LatLngBounds;
    let markerCluster: MarkerClusterer;

    let searchCircle: google.maps.Circle;
    const markers: google.maps.Marker[] = [];
    const markerButtonClassStr = 'v-btn v-btn--block v-theme--light bg-info v-btn--density-default rounded-md v-btn--size-small v-btn--variant-flat';
    const marketsFilterItems = ref<Map<string, GenericVuetifyListItem>>(new Map());

    const milesToMeters = (miles: number) => miles * 1609.344;
    let unWatchDepsLoad: WatchStopHandle;

    // TEMP VARS
    const unit = 'mi';

    useHead({
        script: [
            {
                key: 'googleapis',
                src: `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry`,
                defer: true,
                crossorigin: 'anonymous',
                onload: () => (isMapsLibLoaded.value = true)
            },
            {
                key: 'markerclusterer',
                src: 'https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js',
                defer: true,
                crossorigin: 'anonymous',
                onload: () => (isMarkClusterLibLoaded.value = true)
            }
        ]
    });

    unWatchDepsLoad = watch(
        [
            isMapsLibLoaded,
            isMarkClusterLibLoaded,
            () => props.locations
        ],
        ([maps, clusterer, locations]) => {
            if (maps && clusterer && !lo_isError(locations) && locations?.length) {
                unWatchDepsLoad();
                initMap();
            }
        }
    );

    const unWatchSearch = watch(
        [
            () => props.search,
            mapReady
        ],
        (
            [newSearchObj, newMapReady]: [newSearchObj: LocationsMapSearch, newMapReady: boolean],
            [oldSearchObj, oldMapReady]: [oldSearchObj: Undefinable<LocationsMapSearch>, oldMapReady: Undefinable<boolean>]
        ) => {
            if (map.value && newMapReady) {

                if (
                    !newSearchObj.geolocation &&
                    lo_isNull(oldSearchObj?.geolocation) &&
                    !newSearchObj.location
                ) {
                    reCenterMap();
                } else if (newSearchObj.geolocation) {
                    const { latitude, longitude } = newSearchObj.geolocation.coords;
                    map.value.setZoom(10);
                    map.value.panTo(new google.maps.LatLng(latitude, longitude));
                }

                if (newSearchObj.location) {
                    searchLocation();
                } else {
                    clearSearchRadiusCircle();
                }

                filterMarkersByMarket(newSearchObj.markets || null);
            }
        },
        { immediate: true }
    );

    function initMap(): void {
        let center = { lat: mapCenterCoords[0]!, lng: mapCenterCoords[1]! };
        let zoom = 5;
        let geoEnabled = false;
        const sportGroupSet = new Set<string>();

        if (props.search.geolocation) {
            const { latitude: lat, longitude: lng } = props.search.geolocation.coords;
            center = { lat, lng };
            zoom = 10;
            geoEnabled = true;
        }

        infoWindow = new google.maps.InfoWindow({ content: '' });
        bounds = new google.maps.LatLngBounds();

        map.value = new google.maps.Map(elMap.value!, {
            center,
            zoom,
            disableDefaultUI: true,
            zoomControl: true
        });

        google.maps.event.addListenerOnce(
            map.value,
            'idle',
            () => {
                mapReady.value = true;
            }
        );

        if (lo_isError(props.locations) || !Array.isArray(props.locations)) {
            return;
        }

        props.locations?.forEach((o: Location, i: number) => {
            if (o.address.latitude && o.address.longitude) {
                const location = new google.maps.LatLng(o.address.latitude, o.address.longitude);
                const marker = new google.maps.Marker({
                    position: location,
                    map: map.value,
                    title: o.address.company
                });

                marker.set('uid', o.uid);
                marker.set('sportGroups', o.sportGroups);
                markers.push(marker);
                bounds.extend(location);

                google.maps.event.addListener(
                    marker,
                    'click',
                    (e: google.maps.MapMouseEvent) => openMarker(marker, o)
                );

                o.sportGroups?.forEach((m) => sportGroupSet.add(m));
            }
        });

        const sportGroups = new Map<string, GenericVuetifyListItem>();
        sortSportGroups(Array.from(sportGroupSet)).forEach((m) => {
            sportGroups.set(m, { title: sportGroupLabel(m), value: m });
        });

        marketsFilterItems.value = sportGroups;
        emit('update:markets-filter-list', sportGroups);
        markerCluster = new window.markerClusterer!.MarkerClusterer({ map: map.value, markers, onClusterClick });

        if (!geoEnabled) {
            map.value.fitBounds(bounds, markerBoundsPadding);
        }
    }

    function getInfoWindowBtn(item: Location): string {
        const location: string = '';
        const locStr: string = location ? location.replace(' ', '+') : 'Current+Location';
        const addr = item.address;
        const addrStr: string = addr
            ? [addr.company, addr.street1, addr.street2, addr.city, addr.state, addr.country].filter(Boolean).join('+')
            : '';
        return `<a href="https://www.google.com/maps/dir/${locStr}/${addrStr}/" target="_blank" class="location-marker-button ${markerButtonClassStr}">Directions</a>`;
    }

    function getInfoPhoneLink(item: Location): string {
        const phone = item.phone;
        if (!phone) return '';
        return `
            <div class="text-body-large my-1">
                <a href="tel:${phone}">${phone}</a>
            </div>
        `;
    }

    function getInfoWebLink(item: Location): string {
        const web = item.website;
        if (!web) return '';
        return `
            <div class="text-body-large my-2">
                <a href="${web}" target="_blank">Website</a>
            </div>
        `;
    }

    function getInfoMarketsList(item: Location): string {
        const markets = item.sportGroups;
        if (!markets || !markets.length) return '';

        const marketsList = markets.map((m) => {
            const mObj = marketsFilterItems.value.get(m);
            return mObj ? `<li>${mObj.title}</li>` : '';
        }).join('');

        return `<ul
            v-if="item.markets?.length"
            class="ma-0 pa-0 list-style-none list-comma-sep d-flex text-label-medium text-uppercase"
        >
            ${marketsList}
        </ul>`;
    }

    function openMarker(marker: google.maps.Marker, item: Location): void {
        if (lo_isError(props.locations) || !Array.isArray(props.locations)) {
            return;
        }
        infoWindow.close();
        const content = `
            <div class="location-marker">
                <h2 class="py-0 my-0">${item.address.company}</h2>
                ${getInfoMarketsList(item)}
                <div class="text-body-large my-2">
                    ${item.address.street1} ${item.address.street2 ?? ''}<br>
                    ${item.address.city}, ${item.address.state}<br>
                </div>
                ${getInfoPhoneLink(item)}
                ${getInfoWebLink(item)}
                ${getInfoWindowBtn(item)}
            </div>
        `;

        infoWindow.open({
            anchor: marker,
            map: map.value,
            shouldFocus: true
        });

        infoWindow.setContent(content);
        const latLng = marker.getPosition();

        if (latLng) {
            const mapBounds = map.value!.getBounds();

            if (mapBounds) {
                const isMarkerInBounds = mapBounds.contains(latLng);

                if (!isMarkerInBounds) {
                    const markerBounds = new google.maps.LatLngBounds(latLng);
                    map.value!.fitBounds(markerBounds, markerBoundsPadding);
                    map.value!.setZoom(15);
                }
            }
        }
    }

    function onClusterClick(e: any, cluster: any, map: InstanceType<typeof google.maps.Map>) {
        infoWindow?.close?.();
        map.fitBounds(cluster.bounds);
        return e;
    }

    function onOpenMarker(location: Location): void {
        const { uid } = location;
        const marker = markers.find((m) => m.get('uid') === uid);

        if (marker) {
            openMarker(marker, location);
            return;
        }

        console.info('Marker not found: ', location);
    }

    function clearSearchRadiusCircle(): void {
        searchCircle?.setMap?.(null);
    }

    function drawSearchRadiusCircle(unit: string, radius: number, center: google.maps.LatLng): void {
        clearSearchRadiusCircle();

        radius = unit === 'mi' ? milesToMeters(radius) : radius * 1000;

        searchCircle = new google.maps.Circle({
            map: map.value,
            center,
            radius,
            strokeColor: '#FF0000',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: '#FF0000',
            fillOpacity: 0.08
        });

        const circleBounds = searchCircle?.getBounds?.();

        setTimeout(() => {
            processBoundMarkers(center, radius);
        }, 10);

        if (circleBounds) {
            map.value!.fitBounds(circleBounds);
        }
    }

    function processBoundMarkers(center: google.maps.LatLng, radiusMeters: number) {
        const locationUids = markers.reduce<string[]>((items, marker) => {
            const position = marker.getPosition();

            if (
                position &&
                google.maps.geometry.spherical.computeDistanceBetween(center, position) <= radiusMeters
            ) {
                const uid = marker.get('uid');
                items.push(uid);
            }

            return items;
        }, []);

        emit('on-bound-locations', locationUids);
    }

    function searchLocation(): void {
        const { location, radius, unit } = props.search;

        geocoder.value = new google.maps.Geocoder();
        geocoder.value.geocode(
            { address: location },
            (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
                if (status === 'OK') {
                    if (results && results[0]) {
                        map.value?.setCenter(results[0].geometry.location);
                        drawSearchRadiusCircle(unit || 'mi', radius || 20, results[0].geometry.location);
                    }
                } else {
                    emit('location-search-error');
                }
            }
        );
    }

    function reCenterMap(): void {
        if (map.value) {
            map.value.setZoom(7);
            map.value.panTo(new google.maps.LatLng(mapCenterCoords[0]!, mapCenterCoords[1]));
        }
    }

    function filterMarkersByMarket(selectedMarket: Nilish<string>): void {
        if (!markers) return;

        const visibleMarkers: google.maps.Marker[] = [];

        markers.forEach((marker) => {
            const sportGroups = marker.get('sportGroups') as Nilish<string[]>;
            const visible = selectedMarket
                ? (Array.isArray(sportGroups) && !!sportGroups.length && sportGroups.includes(selectedMarket))
                : true;

            marker.setVisible(visible);
            if (visible) visibleMarkers.push(marker);
        });

        if (markerCluster) {
            markerCluster.clearMarkers();
            markerCluster.addMarkers(visibleMarkers);
        }
    }

    onBeforeUnmount(() => {
        unWatchDepsLoad?.();
        unWatchSearch();
    });

    defineExpose({ onOpenMarker });
</script>

<style lang="scss" scoped>
:deep(.location-marker) {
    color: rgba(var(--v-theme-on-background), var(--v-high-emphasis-opacity));
    padding: 4px;
}
</style>
